/**
 * useFeeQuote Hook
 *
 * Fetches signed fee quotes from the backend for bridge transactions.
 * Quotes include:
 * - Signed operator quote (for contract verification)
 * - Fee breakdown (percentage fee, network fee, net amount)
 * - Expiration timestamp
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { apiKeyHeader } from '@/services/api';

// API endpoint. SAME-ORIGIN by default (D8): fall back to window.location.origin (like useBurn.ts),
// NOT http://localhost:3000 — the beta/prod FE and API share one hostname, so an unset VITE_API_URL
// must resolve to the current origin. If VITE_API_URL is set it MUST be the BARE FE origin, never
// `…/api` (the calls below already append `/api`, so `…/api` would yield `…/api/api` → 404).
const API_BASE = import.meta.env.VITE_API_URL || window.location.origin;

// Types
export interface Quote {
  destDomain: number;
  networkFeeUsdc: string;
  networkFeeNative: string;
  percentageFeeBps: number;
  timestamp: number;
  nonce: number;
  userAddress: string;
  maxFee: string;
  minFinalityThreshold: number;
}

export interface FeeBreakdown {
  grossAmount: string;
  percentageFeeBps: number;
  percentageFee: string;
  networkFeeUsdc: string;
  // Circle's fast-transfer pass-through (deducted from the minted amount); netAmount already subtracts it.
  circleMaxFeeUsdc?: string;
  totalFeeUsdc: string;
  netAmount: string;
  networkFeeNative: string;
  networkFeeNativeDisplay: string;
  sourceChain: string;
  destChain: string;
  nativePrice: number;
}

export interface QuoteState {
  quote: Quote | null;
  signature: string | null;
  breakdown: FeeBreakdown | null;
  expiresAt: number | null;
  isLoading: boolean;
  error: string | null;
}

export interface QuoteParams {
  sourceChain: string;
  destChain: string;
  amount: string; // USDC in smallest units (6 decimals)
  userAddress: string;
}

interface QuoteResponse {
  success: boolean;
  data?: {
    quote: Quote;
    signature: string;
    expiresAt: number;
    breakdown: FeeBreakdown;
  };
  error?: string;
}

/**
 * Fetch a quote from the backend
 */
async function fetchQuoteFromApi(params: QuoteParams): Promise<QuoteResponse> {
  const url = new URL(`${API_BASE}/api/quote`);
  url.searchParams.set('sourceChain', params.sourceChain);
  url.searchParams.set('destChain', params.destChain);
  url.searchParams.set('amount', params.amount);
  url.searchParams.set('userAddress', params.userAddress);

  // Pass referral code from localStorage for fee breakdown accuracy
  const ref = localStorage.getItem('parsebridge:ref');
  if (ref) {
    url.searchParams.set('ref', ref);
  }

  try {
    const response = await fetch(url.toString(), { headers: apiKeyHeader });
    return await response.json();
  } catch {
    return { success: false, error: 'Server unavailable. Please try again later.' };
  }
}

/**
 * Hook for fetching fee quotes
 */
export function useFeeQuote() {
  const [state, setState] = useState<QuoteState>({
    quote: null,
    signature: null,
    breakdown: null,
    expiresAt: null,
    isLoading: false,
    error: null,
  });

  // Track countdown for expiry
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const intervalRef = useRef<number | null>(null);

  // Update countdown
  useEffect(() => {
    if (!state.expiresAt) {
      setTimeRemaining(0);
      return;
    }

    const updateRemaining = () => {
      const now = Math.floor(Date.now() / 1000);
      const remaining = Math.max(0, state.expiresAt! - now);
      setTimeRemaining(remaining);

      if (remaining === 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };

    updateRemaining();
    intervalRef.current = window.setInterval(updateRemaining, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [state.expiresAt]);

  /**
   * Fetch a new quote
   */
  const fetchQuote = useCallback(async (params: QuoteParams) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await fetchQuoteFromApi(params);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch quote');
      }

      setState({
        quote: response.data.quote,
        signature: response.data.signature,
        breakdown: response.data.breakdown,
        expiresAt: response.data.expiresAt,
        isLoading: false,
        error: null,
      });

      return response.data;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Quote failed';
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      throw error;
    }
  }, []);

  /**
   * Check if quote has expired
   */
  const isQuoteExpired = useCallback(() => {
    if (!state.expiresAt) return true;
    return Date.now() / 1000 > state.expiresAt;
  }, [state.expiresAt]);

  /**
   * Get time remaining until expiry
   */
  const getTimeRemaining = useCallback(() => {
    if (!state.expiresAt) return 0;
    return Math.max(0, state.expiresAt - Math.floor(Date.now() / 1000));
  }, [state.expiresAt]);

  /**
   * Check if quote is expiring soon (< 2 minutes)
   */
  const isExpiringSoon = useCallback(() => {
    return timeRemaining > 0 && timeRemaining < 120;
  }, [timeRemaining]);

  /**
   * Clear the current quote
   */
  const clearQuote = useCallback(() => {
    setState({
      quote: null,
      signature: null,
      breakdown: null,
      expiresAt: null,
      isLoading: false,
      error: null,
    });
  }, []);

  /**
   * Format time remaining as MM:SS
   */
  const formatTimeRemaining = useCallback(() => {
    if (timeRemaining <= 0) return '0:00';
    const minutes = Math.floor(timeRemaining / 60);
    const seconds = timeRemaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [timeRemaining]);

  return {
    ...state,
    timeRemaining,
    fetchQuote,
    isQuoteExpired,
    getTimeRemaining,
    isExpiringSoon,
    clearQuote,
    formatTimeRemaining,
  };
}

/**
 * Hook for fetching fee estimates (no signature, for UI preview)
 */
export function useFeeEstimate() {
  const [estimate, setEstimate] = useState<{
    grossAmount: string;
    percentageFeeBps: number;
    percentageFee: string;
    networkFeeUsdc: string;
    totalFeeUsdc: string;
    netAmount: string;
    networkFeeUsd: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEstimate = useCallback(
    async (params: Omit<QuoteParams, 'userAddress'>) => {
      setIsLoading(true);
      setError(null);

      try {
        const url = new URL(`${API_BASE}/api/quote/estimate`);
        url.searchParams.set('sourceChain', params.sourceChain);
        url.searchParams.set('destChain', params.destChain);
        url.searchParams.set('amount', params.amount);

        const response = await fetch(url.toString(), { headers: apiKeyHeader });
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let data: any;
        try {
          data = await response.json();
        } catch {
          throw new Error('Server unavailable. Please try again later.');
        }

        if (!data.success) {
          throw new Error(data.error || 'Failed to fetch estimate');
        }

        setEstimate(data.data.estimate);
        return data.data.estimate;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Estimate failed';
        setError(errorMessage);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const clearEstimate = useCallback(() => {
    setEstimate(null);
    setError(null);
  }, []);

  return {
    estimate,
    isLoading,
    error,
    fetchEstimate,
    clearEstimate,
  };
}

/**
 * Fetch fee configuration (public)
 */
export async function getFeeConfig(): Promise<{
  baseBps: number;
  maxFeeBps: number;
  minAmountUsd: number;
  quoteValiditySeconds: number;
  minAmountByDest: Record<string, number>;
}> {
  const response = await fetch(`${API_BASE}/api/quote/config`, { headers: apiKeyHeader });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let data: any;
  try {
    data = await response.json();
  } catch {
    throw new Error('Server unavailable. Please try again later.');
  }

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch fee config');
  }

  return {
    baseBps: data.data.fees.baseBps,
    maxFeeBps: data.data.fees.maxFeeBps,
    minAmountUsd: data.data.fees.minAmountUsd ?? data.data.fees.minAmountUsdc ?? 1,
    quoteValiditySeconds: data.data.quoteValiditySeconds,
    minAmountByDest: data.data.fees.minAmountUsdByDest || data.data.fees.minAmountByDest || {},
  };
}
